import {Kind} from 'graphql';
import {simpleHash} from 'src/internal/revamp/cachingUtils/simpleHash';
import {isDefinedOnQuery} from 'src/internal/revamp/graphql/documentUtils/composableFragment';
import {findEntryPointDefinition} from 'src/internal/revamp/graphql/documentUtils/entrypoint';

import type {
  ArgumentNode,
  DefinitionNode,
  DocumentNode,
  FragmentDefinitionNode,
  OperationDefinitionNode,
  SelectionNode,
  SelectionSetNode,
} from 'graphql';

const hashCache = new Map<DocumentNode, string>();

// the hashes that we calculate with this helper serve a few purposes:
// - Enables the fetching pipeline to identify fragments and queries with a hash. A fragment and its
//   associated query always obtain the same hash result. This assures that the hashes we calculate
//   for each top level field in useRootQuery can then be reproducible in useFragment call.
// - Top level fields and variables are prefixed by a chain of hashes. This prevents collisions
//   between variables inside a composition fragment thanks to hashes being deterministic.
//
// The documents that it can calculate the hash for are fragmets defined `on Query` and Query
// operations themselves.
//
export function generateHashForSelectionSet(
  selectionSet: SelectionSetNode,
  allDefinitions: readonly DefinitionNode[],
): string {
  const fieldsToHash = findTopLevelFieldSelectionsToHash(
    selectionSet,
    allDefinitions,
  );
  return simpleHash(fieldsToHash);
}

export function generateHashForDefinitionNode(
  document: DocumentNode,
  definitionNode: OperationDefinitionNode | FragmentDefinitionNode,
): string {
  return generateHashForSelectionSet(
    definitionNode.selectionSet,
    document.definitions,
  );
}

export function generateHashForDocument(document: DocumentNode): string {
  const existingHash = hashCache.get(document);
  if (existingHash) {
    return existingHash;
  }

  const definitionNode = findEntryPointDefinition(document);
  const hash = generateHashForDefinitionNode(document, definitionNode);

  hashCache.set(document, hash);

  return hash;
}

function findTopLevelFieldSelectionsToHash(
  selectionSet: SelectionSetNode,
  allDefinitions: readonly DefinitionNode[],
): string {
  const {selections} = selectionSet;

  const selectionsToTakeIntoAccount = selections.filter((selection) => {
    // Ignore spreaded fragments if they are defined on Query
    if (selection.kind === 'FragmentSpread') {
      const fragmentDefinition = getFragmentByName(
        selection.name.value,
        allDefinitions,
      );

      if (isDefinedOnQuery(fragmentDefinition)) {
        return false;
      }

      return true;
    }
    return true;
  });

  return selectionsToTakeIntoAccount
    .map((selection) => serializeSelection(selection, allDefinitions))
    .filter((serialized) => serialized !== '') // Filter out empty strings from ignored __typename fields
    .sort()
    .join(',');
}

function getFragmentByName(
  name: string,
  allDefinitions: readonly DefinitionNode[],
): FragmentDefinitionNode {
  const matchedDefinition = allDefinitions.find(
    (def) => 'name' in def && def.name?.value === name,
  );
  if (!matchedDefinition) {
    throw new Error(`Fragment ${name} not found`);
  }

  if (matchedDefinition.kind !== Kind.FRAGMENT_DEFINITION) {
    throw new Error(`Fragment ${name} not found`);
  }

  return matchedDefinition;
}

function serializeSelection(
  selection: SelectionNode,
  allDefinitions: readonly DefinitionNode[],
): string {
  if (selection.kind === 'Field') {
    const fieldName = selection.name.value;

    // Ignore __typename fields for hashing purposes
    if (fieldName === '__typename') {
      return '';
    }

    const fieldData = selection.selectionSet
      ? `:${serializeSelectionSet(selection.selectionSet, allDefinitions)}`
      : '';
    const argumentsData = selection.arguments
      ? `(${serializeFieldArguments(selection.arguments)})`
      : '';
    return `${fieldName}${fieldData}${argumentsData}`;
  }

  if (selection.kind === 'FragmentSpread') {
    const spreadFragment = getFragmentByName(
      selection.name.value,
      allDefinitions,
    );

    return serializeSelectionSet(spreadFragment.selectionSet, allDefinitions);
  }

  if (selection.kind === 'InlineFragment') {
    if (selection.typeCondition?.name.value === 'Query') {
      // `... on Query` is just a composition wrapper, like spreading another
      // fragment defined on Query. Ignoring it here keeps the parent hash
      // stable across the old named-fragment syntax and the new inline syntax;
      // the inline fragment's own selections are hashed separately when that
      // branch becomes its own transformed namespace.
      return '';
    }

    return serializeSelectionSet(selection.selectionSet, allDefinitions);
  }

  return '';
}

function serializeSelectionSet(
  selectionSet: SelectionSetNode,
  allDefinitions: readonly DefinitionNode[],
): string {
  return selectionSet.selections
    .map((selection) => serializeSelection(selection, allDefinitions))
    .filter((serialized) => serialized !== '') // Filter out empty strings from ignored __typename fields
    .sort()
    .join(',');
}

function serializeFieldArguments(
  fieldArguments: readonly ArgumentNode[],
): string {
  return fieldArguments
    .map((argument) => argument.name.value)
    .sort()
    .join(',');
}
